import { Dispatch } from "redux";
import { GrossMarginReport, IGrossMarginReportQuery } from "models/gross-margin-report";
import { setReport } from "./reducer";
import { toast } from "react-toastify";


export const downloadGrossMarginReportCSV = (report: GrossMarginReport | null, query: IGrossMarginReportQuery) => {
    const fmt = (val: number | null | undefined) =>
        val == null ? '0.00' : val.toLocaleString('en-CA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    const fmtPct = (val: number | null | undefined) =>
        val == null ? '0.00%' : (val * 100).toFixed(2) + '%'
    const varD = (a: number | null, b: number | null) => (a ?? 0) - (b ?? 0)
    const varPct = (a: number | null, b: number | null) => b ? ((a ?? 0) - (b ?? 0)) / Math.abs(b) : null

    const r = report
    const consultingRevenueActual = (r?.consultingIncomeActual ?? 0) + (r?.accruedRevenueActual ?? 0)
    const consultingRevenueBudget = r?.consultingIncomeBudget ?? 0
    const consultingCogsActual = (r?.directHourlyCostActual ?? 0) + (r?.subConsultantActual ?? 0)
    const consultingCogsBudget = (r?.directHourlyCostBudget ?? 0) + (r?.subConsultantBudget ?? 0)
    const consultingRevenueGMActual = consultingRevenueActual - consultingCogsActual
    const consultingRevenueGMBudget = consultingRevenueBudget - consultingCogsBudget
    const gmPctActual = r?.grossMarginActual && r?.totalRevenueActual ? r.grossMarginActual / r.totalRevenueActual : null
    const gmPctBudget = r?.grossMarginBudget && r?.totalRevenueBudget ? r.grossMarginBudget / r.totalRevenueBudget : null
    const gmNetPctActual = r?.gmNetRevenueActual && r?.netRevenueActual ? r.gmNetRevenueActual / r.netRevenueActual : null
    const gmNetPctBudget = r?.grossMarginBudget && r?.netRevenueBudget ? r.grossMarginBudget / r.netRevenueBudget : null

    type CsvRow = string[]
    const row = (label: string, actual: number | null, budget: number | null = null): CsvRow => [
        label, fmt(actual), fmt(budget), fmt(varD(actual, budget)), fmtPct(varPct(actual, budget)),
    ]
    const pctRow = (label: string, actual: number | null, budget: number | null = null): CsvRow => [
        label, fmtPct(actual), fmtPct(budget), fmtPct((actual ?? 0) - (budget ?? 0)), fmtPct(varPct(actual, budget)),
    ]
    const section = (label: string): CsvRow => [label, '', '', '', '']

    const rows: CsvRow[] = [
        ['Label', 'Actual', 'Budget', 'Variance $', 'Variance %'],
        section('Revenue'),
        section('Sales Revenue'),
        row('Consulting Income', r?.consultingIncomeActual ?? null, r?.consultingIncomeBudget ?? null),
        row('Disbursement Income', r?.disbursementIncomeActual ?? null, r?.disbursementIncomeBudget ?? null),
        row('Accrued Revenue', r?.accruedRevenueActual ?? null),
        row('Accrued Disbursement Revenue', r?.accruedDisbursementActual ?? null),
        row('Total Sales Revenue', r?.totalSalesRevenueActual ?? null, r?.totalSalesRevenueBudget ?? null),
        section('Disbursement Cost'),
        row('Direct Costs', r?.directCostActual ?? null, r?.directCostBudget ?? null),
        row('Accrued Cost', r?.accruedCostActual ?? null),
        row('Total Disbursement Cost', r?.totalDisbursementCostActual ?? null, r?.totalDisbursementCostBudget ?? null),
        row('Net Revenue', r?.netRevenueActual ?? null, r?.netRevenueBudget ?? null),
        section('Other Revenue'),
        row('HO Support Revenue', r?.hoSupportRevenueActual ?? null),
        row('Rental Income', r?.rentalIncomeActual ?? null),
        row('Total Revenue', r?.totalRevenueActual ?? null, r?.totalRevenueBudget ?? null),
        section('Cost of Revenue'),
        row('Direct Hourly Cost', r?.directHourlyCostActual ?? null, r?.directHourlyCostBudget ?? null),
        row('Sub-consultant', r?.subConsultantActual ?? null, r?.subConsultantBudget ?? null),
        row('Equipment Hour', r?.equipmentHourActual ?? null, r?.equipmentHourBudget ?? null),
        row('Direct Payroll Cost', r?.directPayrollCostActual ?? null),
        row('Accrued Direct Cost', r?.accruedDirectCostActual ?? null),
        row('Total Cost of Consulting Revenue', r?.totalCostOfRevenueActual ?? null, r?.totalCostOfRevenueBudget ?? null),
        row('Gross Margin', r?.grossMarginActual ?? null, r?.grossMarginBudget ?? null),
        pctRow('GM%', gmPctActual, gmPctBudget),
        row('GM (Net Revenue)', r?.gmNetRevenueActual ?? null, r?.grossMarginBudget ?? null),
        pctRow('GM % (Net Revenue)', gmNetPctActual, gmNetPctBudget),
        section('Consulting Revenue Breakdown'),
        row('Consulting Revenue', consultingRevenueActual, consultingRevenueBudget),
        row('Consulting COGS', consultingCogsActual, consultingCogsBudget),
        row('Consulting Revenue GM', consultingRevenueGMActual, consultingRevenueGMBudget),
        pctRow('Consulting Revenue GM %',
            consultingRevenueActual ? consultingRevenueGMActual / consultingRevenueActual : null,
            consultingRevenueBudget ? consultingRevenueGMBudget / consultingRevenueBudget : null
        ),
    ]

    const csv = rows
        .map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(','))
        .join('\n')

    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `gross-margin-report-${query.fromFiscalPeriod}-${query.toFiscalPeriod}.csv`
    a.click()
    URL.revokeObjectURL(url)
}

export const getGrossMarginReport = (query: IGrossMarginReportQuery) => async (dispatch: Dispatch) => {
    try {
        let queryString = '';

        if (query) {
            const params = new URLSearchParams();

            Object.entries(query).forEach(([key, value]) => {
                if (value === undefined || value === null || value === '') return;

                if (Array.isArray(value)) {
                    value.forEach(v => {
                        if (v) params.append(key, v);
                    });
                } else {
                    params.append(key, String(value));
                }
            });

            queryString = params.toString();
        }

        const url = `${process.env.REACT_APP_API_URL}/gross-margin-report${queryString ? `?${queryString}` : ''}`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }
        const data = await res.json();
        dispatch(setReport(data.data));
    } catch (err: any) {
        toast.error(err.message ?? "Error getting projects");
    }
}



export const getGrossMarginReportSearchFields = async () => {
    try {
        const url = `${process.env.REACT_APP_API_URL}/gross-margin-report/search-fields`;

        const res = await fetch(url, {
            credentials: 'include',
            method: 'GET',
        });

        if (!res.ok) {
            const errBody = await res.json();
            throw new Error(errBody.message);
        }

        const data = await res.json();
        return data.data;
    } catch (err: any) {
        toast.error(err.message ?? "Error getting gross margin report search fields");
        return null;
    }
}

export const syncGrossMarginData = async (): Promise<boolean> => {
    try {
        
        const url = `${process.env.REACT_APP_API_URL}/gross-margin-report/sync`;
        const res = await fetch(url, {
            credentials: 'include',
            method: 'POST'
        });

        if (!res.ok) {
            const errRes = await res.json();
            toast.error(errRes.message);
            return false
        }
        toast.success("Sync started. The page will refresh automatically when it finishes.")
        return true
    } catch (err: any) {

        toast.error("Something went wrong. Could not sync data")
        console.error('Error syncing data:', err);
        return false
    }
} 

export interface GrossMarginBreakdownRow {
    departmentId: string
    department: string
    branchId: string
    branch: string
    amount: number
}

// GET /gross-margin-report/breakdown: department x branch amounts behind one value of the report
export const getGrossMarginBreakdown = async (query: IGrossMarginReportQuery, field: string): Promise<GrossMarginBreakdownRow[] | null> => {
    try {
        const params = new URLSearchParams({ field })
        Object.entries(query).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') return
            if (Array.isArray(value)) value.forEach(v => v && params.append(key, v))
            else params.append(key, String(value))
        })
        const res = await fetch(`${process.env.REACT_APP_API_URL}/gross-margin-report/breakdown?${params}`, {
            credentials: 'include',
            method: 'GET',
        })
        if (!res.ok) {
            const errBody = await res.json().catch(() => null)
            throw new Error(errBody?.message ?? "Could not load the breakdown")
        }
        const data = await res.json()
        return data.data
    } catch (err: any) {
        toast.error(err.message ?? "Could not load the breakdown")
        return null
    }
}
